/**
 * Вход по коду (§12.2, §12.7 тексты отказов).
 *
 * Пароли оказались лишним звеном: администратор присылает человеку ссылку и
 * шесть цифр — больше ничего вводить не нужно. Код можно передать и прямо в
 * ссылке (`/login?code=123456`), тогда вход происходит сам.
 *
 * Вход по паролю остался запасным: он скрыт за ссылкой внизу и нужен, если
 * коды разом потеряются, — иначе в систему будет не попасть вообще.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CodeInput, Logo } from '../../components/index.js'
import { api, ApiRequestError } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { texts } from '../../texts.js'
import './auth.css'
import './LoginScreen.css'

interface LoginLocationState {
  reason?: 'expired'
  from?: string
}

export function LoginScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = (location.state ?? null) as LoginLocationState | null

  const [code, setCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [byPassword, setByPassword] = useState(false)
  const [shake, setShake] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const emailRef = useRef<HTMLInputElement>(null)

  /* Автофокус: на форме с паролем — в почту, на коде им занимается сама
     первая ячейка (CodeInput autoFocus). */
  useEffect(() => {
    if (byPassword) emailRef.current?.focus()
  }, [byPassword])

  /** Код из ссылки: входим сразу, человеку не нужно ничего перепечатывать. */
  useEffect(() => {
    const fromLink = new URLSearchParams(location.search).get('code')?.trim() ?? ''
    if (!/^\d{6}$/.test(fromLink)) return
    setCode(fromLink)
    void enter(fromLink)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  async function enter(value: string): Promise<void> {
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      await api.loginByCode(value)
      await useApp.getState().load()
      navigate(state?.from ?? '/brands', { replace: true })
    } catch (err) {
      setBusy(false)
      setError(err instanceof Error ? err.message : texts.fail.server)
      /* Код набирают с голоса и часто ошибаются одной цифрой. Чистим ячейки и
         возвращаем курсор в первую, чтобы не пришлось стирать шесть цифр. */
      setCode('')
      setShake(true)
      window.setTimeout(() => setShake(false), 400)
      document.querySelector<HTMLInputElement>('.code-input__cell')?.focus()
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (busy) return
    setError(null)
    setBusy(true)
    try {
      await api.login(email.trim(), password)
      await useApp.getState().load()
      navigate(state?.from ?? '/brands', { replace: true })
    } catch (err) {
      setBusy(false)
      setError(
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : texts.fail.server,
      )
    }
  }

  return (
    <div className="auth auth--login">
      <div className="auth__box">
        <span className="auth__brand"><Logo height={28} /></span>
        <h1 className="auth__title">{texts.screen.login.title}</h1>

        {state?.reason === 'expired' && (
          <p className="auth__notice" role="status">{texts.fail.sessionExpired}</p>
        )}

        {!byPassword ? (
          <form
            className="auth__form"
            noValidate
            onSubmit={(event) => { event.preventDefault(); void enter(code.trim()) }}
          >
            {error && <p className="auth__error" role="alert">{error}</p>}

            <div className="auth-field">
              <span className="auth-field__label">{texts.auth.code}</span>
              <CodeInput
                value={code}
                label={texts.auth.code}
                disabled={busy}
                invalid={shake}
                autoFocus
                onChange={(next) => { setCode(next); setError(null) }}
                /* Шесть цифр — вводить больше нечего, входим сразу. */
                onComplete={(next) => { void enter(next) }}
              />
            </div>

            <p className="auth__hint">{texts.auth.codeHint}</p>

            <button className="auth__submit" type="submit" disabled={busy || code.length < 6}>
              {busy && <span className="auth__spinner" aria-hidden="true" />}
              {busy ? texts.auth.submitting : texts.auth.submit}
            </button>

            <button type="button" className="auth__alt" onClick={() => { setByPassword(true); setError(null) }}>
              {texts.auth.byPassword}
            </button>
          </form>
        ) : (
        <form className="auth__form" onSubmit={submit} noValidate>
          {error && <p className="auth__error" role="alert">{error}</p>}

          <label className="auth-field">
            <span className="auth-field__label">{texts.auth.email}</span>
            <input
              ref={emailRef}
              className="auth-field__input"
              type="email"
              name="email"
              value={email}
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              disabled={busy}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>

          <label className="auth-field">
            <span className="auth-field__label">{texts.auth.password}</span>
            <input
              className="auth-field__input"
              type="password"
              name="password"
              value={password}
              autoComplete="current-password"
              required
              disabled={busy}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>

          {/* Кнопка остаётся в tab-порядке и в загрузке: ширина не меняется (§2.3). */}
          <button className="auth__submit" type="submit" disabled={busy}>
            {busy && <span className="auth__spinner" aria-hidden="true" />}
            {busy ? texts.auth.submitting : texts.auth.submit}
          </button>

          <button type="button" className="auth__alt" onClick={() => { setByPassword(false); setError(null) }}>
            {texts.auth.byCode}
          </button>
        </form>
        )}
      </div>
    </div>
  )
}
